import { useCallback, useRef, useState } from "react";
import { captureRegion, copyToClipboard, downloadInstead, groundColour } from "./capture";

// Choosing a region, and saying what happened.
//
// Clipboard writes fail for reasons a reader can do something about — an
// insecure origin, a browser that will not, a permission refused — so the
// result is announced rather than swallowed. A button that silently does
// nothing is worse than one that says it could not.
export default function useCapture(svgRef) {
  const [arming, setArming] = useState(false);
  const [marquee, setMarquee] = useState(null);
  const [status, setStatus] = useState(null);
  const origin = useRef(null);

  // The clipboard refuses for reasons a reader can do nothing about from here
  // — an insecure origin, a browser that will not, a window that lost focus.
  // The picture still exists, so it is offered as a file rather than lost.
  const fallBack = useCallback((picture, error) => {
    downloadInstead(picture)
      .then(() => setStatus({
        ok: true,
        message: `Saved it as a file instead — ${error.message}.`
      }))
      .catch(() => setStatus({
        ok: false,
        message: `Could not copy it: ${error.message}.`
      }));
  }, []);

  const arm = useCallback(() => {
    setStatus(null);
    setArming((was) => !was);
    setMarquee(null);
  }, []);

  const onPointerDown = useCallback((event) => {
    if (!arming) return false;
    event.preventDefault();
    origin.current = { x: event.clientX, y: event.clientY };
    setMarquee({ left: event.clientX, top: event.clientY, width: 0, height: 0 });
    return true;
  }, [arming]);

  const onPointerMove = useCallback((event) => {
    if (!arming || !origin.current) return false;
    const start = origin.current;
    setMarquee({
      left: Math.min(start.x, event.clientX),
      top: Math.min(start.y, event.clientY),
      width: Math.abs(event.clientX - start.x),
      height: Math.abs(event.clientY - start.y)
    });
    return true;
  }, [arming]);

  // NOT async, and that is the whole point: the clipboard write has to begin
  // inside the gesture that asked for it. Awaiting the picture first — which
  // is what this did — meant the browser refused a write it had authorised
  // moments earlier, and the picture was drawn correctly and thrown away.
  const onPointerUp = useCallback(() => {
    if (!arming || !origin.current) return false;
    origin.current = null;

    const chosen = marquee;
    setMarquee(null);
    setArming(false);

    // A stray click is not a region.
    if (!chosen || chosen.width < 12 || chosen.height < 12) {
      setStatus({ ok: false, message: "Drag a box over the part you want." });
      return true;
    }

    const picture = captureRegion(svgRef.current, chosen, { background: groundColour() });
    // The promise is handed straight to the clipboard, which resolves it
    // itself while the gesture is still good for it.
    picture.catch(() => {});

    try {
      copyToClipboard(picture)
        .then(() => setStatus({ ok: true, message: "Copied. Paste it wherever you like." }))
        .catch((error) => fallBack(picture, error));
    } catch (error) {
      fallBack(picture, error);
    }
    return true;
  }, [arming, marquee, svgRef, fallBack]);

  return { arm, arming, marquee, onPointerDown, onPointerMove, onPointerUp, status };
}
